import React, { Fragment, ReactNode, useCallback, useEffect } from "react";
import { TApiParameter } from "../../api/endpoints/Parameter";
import { TApiSubset } from "../../api/endpoints/Subset";
import { TApiSet } from "../../api/endpoints/Set";
import { isEmpty } from "lodash";
import { Form } from "react-bootstrap";
import AreaSelector from "./AreaSelector";
import Footnote from "../RealtimeProductConfigurator/Footnote";
import Link from "../Navigation/Links/Link";
import { paths } from "../../router";
import { OrderCategory } from "../../api/endpoints/Order";
import { useSelector } from "react-redux";
import { selectParameters } from "../../store/selectors/new/parameters";
import { TRootState } from "../../store/reducers/RootReducer";
import LoadingIndicator from "../Feedback/LoadingIndicator";
import EuCutOutProductHelpers from "../../utils/EuCutOutProductHelpers";
import { FormikHelpers, FormikProps, useFormik } from "formik";
import FormError from "../RealtimeProductConfigurator/FormError";
import ParametersSelector from "./ParametersSelector";
import HighFrequencyProductsSelector from "./HighFrequencyProductsSelector";
import BeforeUnload from "../Feedback/BeforeUnload";
import { IEuCutOutProductData, TOrderProduct } from "../../api/endpoints/OrderProduct";

export type TParameterSelection = {
  parameter: TApiParameter;
  subset: TApiSubset;
  set: TApiSet;
}

export type SelectionState = Record<string, TParameterSelection>;

const PARAMETER_LIMIT = 15;

interface IFormValues {
  parameterSelection: SelectionState;
  highFrequency: boolean;
}

type TFormErrors = {
  [key in keyof IFormValues]?: string | undefined;
}

const validate = (values: IFormValues): TFormErrors => {
  const errors: TFormErrors = {};

  if (Object.keys(values.parameterSelection).length === 0) {
    errors.parameterSelection = 'At least one parameter is required.';
  }

  if (Object.keys(values.parameterSelection).length > PARAMETER_LIMIT) {
    errors.parameterSelection = 'Too many selected parameters.';
  }

  return errors;
}

const formValuesToProducts = (values: IFormValues) => {
  return [
    {
      param: Object.values(values.parameterSelection).map(item => ({
        value: item.parameter.shortname,
        subset_id: item.subset.id
      })),
      high_frequency: values.highFrequency
    }
  ]
}

interface IProps {
  product?: TOrderProduct<OrderCategory.EU_CUTOUT>;
  actions?: (products: Array<IEuCutOutProductData>, formik: FormikProps<IFormValues>) => ReactNode;
  onSubmit?: (values: Array<IEuCutOutProductData>, formik: FormikHelpers<IFormValues>) => void;
}

const EuCutOutConfigurator = (props: IProps) => {
  const { product, actions, onSubmit } = props;

  const allParameters = useSelector(selectParameters);
  const sets = useSelector<TRootState, Array<TApiSet>>(state => state.sets.data);

  const formik = useFormik<IFormValues>({
    initialValues: {
      parameterSelection: {},
      highFrequency: product?.data.high_frequency ?? false
    },
    isInitialValid: false,
    validate,
    onSubmit: (values, formikHelpers) => {
      onSubmit && onSubmit(formValuesToProducts(values), formikHelpers);
    }
  });

  useEffect(() => {
    if (product && !isEmpty(allParameters) && !isEmpty(sets)) {
      formik.setFieldValue(
        'parameterSelection',
        EuCutOutProductHelpers.deriveParameterSelection(product, sets, allParameters)
      )
    }
  }, [product, allParameters, sets]);

  const onParametersChange = useCallback((selection: SelectionState) => {
    formik.setFieldValue('parameterSelection', selection);
  }, [formik]);

  const onHighFrequencyChange = useCallback((value) => {
    formik.setFieldValue('highFrequency', value);
  }, [formik]);

  if (isEmpty(sets) && isEmpty(allParameters)) {
    return <LoadingIndicator />;
  }

  return (
    <BeforeUnload enabled={formik.dirty && !formik.isSubmitting}>
      <Form onSubmit={formik.handleSubmit} className="eu-cut-out-configurator product-form">
        <Form.Group controlId="highFrequencyProducts">
          <Form.Label>Parameters</Form.Label>

          <div className="selected-parameters">
            <ParametersSelector
              values={formik.values.parameterSelection}
              limit={PARAMETER_LIMIT}
              onChange={onParametersChange}
            />

            <FormError
              // @ts-ignore
              message={formik.errors.parameterSelection}
              when={formik.touched.parameterSelection && Boolean(formik.errors.parameterSelection)}
            />

            <Footnote
              text={
                <Fragment>
                  If you want to configure more than 15 parameters, please configure a quote for a standard-commercial
                  licence or maximum charge licence,&nbsp;
                  <Link to={paths.realtimeOrderNew}>here</Link>.
                </Fragment>
              }
            />
          </div>
        </Form.Group>


        <Form.Group controlId="highFrequencyProducts">
          <Form.Label>Areas</Form.Label>

          <AreaSelector />

          <Footnote
            text={
              <Fragment>
                If you want to configure data outside of the area 25°N-84°N, 74°W-45°E, please configure a quote for a
                standard-commercial licence or maximum charge licence,&nbsp;
                <Link to={paths.realtimeOrderNew}>here</Link>.
              </Fragment>
            }
          />
        </Form.Group>

        <Form.Group controlId="highFrequencyProducts">
          <Form.Label>High frequency products</Form.Label>

          <div>
            <HighFrequencyProductsSelector
              value={formik.values.highFrequency}
              onChange={onHighFrequencyChange}
            />
          </div>

          <FormError
            message={formik.errors.highFrequency}
            when={formik.touched.highFrequency && Boolean(formik.errors.highFrequency)}
          />
        </Form.Group>

        <div className="actions">
          {
            actions &&
            actions(formValuesToProducts(formik.values), formik)
          }
        </div>
      </Form>
    </BeforeUnload>
  )
}

export default EuCutOutConfigurator;
